/**
 * Hydrate nodes that carry `variant="..."` attributes with data from a
 * currently-selected `ProductVariant`.
 *
 * Supported values:
 *   image | sku | barcode | weight | unit-price | stock | discount-percentage
 *   discounted-amount | option1-name | option1-value | option2-name | option2-value
 *   option3-name | option3-value | url
 */

import { config } from "../config.js";
import { formatMoney } from "../ui/format.js";
import type { Product, ProductVariant, ShopifyImage } from "../types.js";

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

/**
 * Resize URL Shopify CDN via param `?width=` pour reduire le poids des
 * images cards de ~80% (200KB → 30-50KB). Skip si pas Shopify CDN ou
 * si width deja present.
 */
function shopifyResize(url: string, width: number): string {
  if (typeof url !== "string" || url.length === 0) return url;
  if (!/cdn\.shopify\.com/.test(url)) return url;
  if (/[?&]width=/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
}

function applyImage(el: Element, image: ShopifyImage | null): void {
  if (!image) return;
  if (el instanceof HTMLImageElement) {
    // Card image ~250-400px display → resize 800 (Retina x2 + quality).
    el.src = shopifyResize(image.url, 800);
    if (image.altText) el.alt = image.altText;
    // NE PAS poser les attrs HTML `width`/`height` en px : ils créent un conflit
    // avec les CSS `aspect-ratio` / `width:100%` authorés côté Webflow — le
    // browser utilise alors la height de l'attribut HTML plutôt que celle
    // dérivée de l'aspect-ratio, ce qui étire les cards (ex: card 2258px de
    // haut au lieu de 232px sur une image 2000×2000 dans une grille 6 cols).
    // On s'appuie sur le CSS Webflow (aspect-ratio + object-fit:cover) pour
    // le sizing ; le CLS est évité au niveau author-CSS.
    el.removeAttribute("width");
    el.removeAttribute("height");
    // Lazy loading natif pour les cards hors viewport.
    // (pas de decoding=async : cree trop de decodes concurrents qui janks
    // les animations Webflow IX2 comme le banner marquee sur /all-products)
    // Sauf si la card est marquee `data-lmdr-priority="high"` (8 premieres) :
    // alors loading=eager + fetchpriority=high pour LCP rapide.
    const card = el.closest("smootify-product");
    const isHighPrio = card?.getAttribute("data-lmdr-priority") === "high";
    if (isHighPrio) {
      el.setAttribute("loading", "eager");
      el.setAttribute("fetchpriority", "high");
    } else if (!el.hasAttribute("loading")) {
      el.setAttribute("loading", "lazy");
    }
    // Skeleton CSS shimmer reste actif jusqu'a ce que la NOUVELLE image
    // soit decodee. ATTENTION : `el.complete` peut etre true a cause de
    // l ancienne placeholder Webflow deja chargee — on attend toujours
    // l event `load` du nouveau src. On stocke l URL cible et compare au
    // load pour ignorer les load events anciens.
    if (card) card.removeAttribute("data-lmdr-img-ready"); // reset si re-hydratation
    const expectedSrc = el.src;
    // Guard idempotent : load event + rAF-complete path peuvent tous deux
    // appeler markReady pour une image cachee. Sans ce flag on set l attribut
    // 2x et (pire) on consomme 2 slots de cascade pour rien — visible a l oeil
    // comme un gap de 100ms vide.
    let ready = false;
    const markReady = (): void => {
      if (ready) return;
      if (el.src !== expectedSrc || !card) return;
      ready = true;
      card.setAttribute("data-lmdr-img-ready", "1");
    };
    el.addEventListener("load", markReady, { once: true });
    el.addEventListener("error", markReady, { once: true });
    // Si l image est deja loaded apres assignation src (cas browser cache),
    // les events peuvent ne pas fire dans la meme tick → check apres rAF.
    requestAnimationFrame(() => {
      if (el.complete && el.naturalWidth > 0 && el.src === expectedSrc) {
        markReady();
      }
    });
  } else if (el instanceof HTMLElement) {
    el.style.backgroundImage = `url(${JSON.stringify(shopifyResize(image.url, 800))})`;
    const card = el.closest("smootify-product");
    if (card) card.setAttribute("data-lmdr-img-ready", "1");
  }
}

function setText(el: Element, text: string): void {
  el.textContent = text;
}

function productUrl(handle: string): string {
  const base = config.productsBase.replace(/^\/+|\/+$/g, "");
  return `/${base}/${encodeURIComponent(handle)}`;
}

function optionByIndex(variant: ProductVariant, idx: number) {
  return variant.selectedOptions[idx] ?? null;
}

function discountPercentage(variant: ProductVariant): number | null {
  if (!variant.compareAtPrice) return null;
  const base = Number(variant.compareAtPrice.amount);
  const price = Number(variant.price.amount);
  if (!Number.isFinite(base) || !Number.isFinite(price) || base <= 0) return null;
  return Math.max(0, Math.round((1 - price / base) * 100));
}

function discountedAmount(variant: ProductVariant) {
  if (!variant.compareAtPrice) return null;
  const base = Number(variant.compareAtPrice.amount);
  const price = Number(variant.price.amount);
  if (!Number.isFinite(base) || !Number.isFinite(price)) return null;
  const delta = base - price;
  if (delta <= 0) return null;
  return {
    amount: delta.toFixed(2),
    currencyCode: variant.price.currencyCode,
  };
}

// ---------------------------------------------------------------------------
//  Public
// ---------------------------------------------------------------------------

export interface HydrateVariantOptions {
  root: ParentNode;
  product: Product;
  variant: ProductVariant;
}

export function hydrateVariant({ root, product, variant }: HydrateVariantOptions): void {
  // Smootify utilise `variation=` dans les templates Webflow comme alias de
  // `variant=` (observé sur les cards related / last-viewed / main-product).
  // On supporte les 2 — `variant=` prend priorité si les 2 sont présents.
  const nodes = root.querySelectorAll<HTMLElement>("[variant], [variation]");

  nodes.forEach((el) => {
    const kind = el.getAttribute("variant") ?? el.getAttribute("variation") ?? "";

    switch (kind) {
      case "image": {
        // variant.image si présente, sinon fallback sur la 1re image produit
        // (utile pour les cards related où on n'a pas forcément de variant image).
        const img = variant.image ?? product.images[0] ?? null;
        if (img) {
          const target =
            el instanceof HTMLImageElement ? el : el.querySelector("img") ?? el;
          applyImage(target, img);
        }
        break;
      }

      case "sku":
        setText(el, variant.sku ?? "");
        break;

      case "barcode":
        setText(el, variant.barcode ?? "");
        break;

      case "weight":
        // The ProductVariant type does not currently expose weight; noop.
        break;

      case "unit-price":
        setText(el, formatMoney(variant.price));
        break;

      case "stock": {
        const qty = variant.quantityAvailable;
        // Si la variant n'est pas dispo (availableForSale=false), on force "0"
        // pour refléter la vraie situation côté Shopify — sinon l'ancien "2"
        // hardcoded dans le template Webflow resterait affiché.
        if (!variant.availableForSale) {
          setText(el, "0");
        } else {
          setText(el, qty == null ? "" : String(qty));
        }
        break;
      }

      case "discount-percentage": {
        const pct = discountPercentage(variant);
        if (pct != null) {
          setText(el, `-${pct}%`);
          el.style.removeProperty("display");
        } else {
          el.style.display = "none";
        }
        break;
      }

      case "discounted-amount": {
        const delta = discountedAmount(variant);
        if (delta) {
          setText(el, formatMoney(delta));
          el.style.removeProperty("display");
        } else {
          el.style.display = "none";
        }
        break;
      }

      case "url": {
        const href = productUrl(product.handle);
        if (el instanceof HTMLAnchorElement) el.href = href;
        else el.setAttribute("href", href);
        break;
      }

      case "option1-name":
        setText(el, optionByIndex(variant, 0)?.name ?? "");
        break;
      case "option1-value":
        setText(el, optionByIndex(variant, 0)?.value ?? "");
        break;
      case "option2-name":
        setText(el, optionByIndex(variant, 1)?.name ?? "");
        break;
      case "option2-value":
        setText(el, optionByIndex(variant, 1)?.value ?? "");
        break;
      case "option3-name":
        setText(el, optionByIndex(variant, 2)?.name ?? "");
        break;
      case "option3-value":
        setText(el, optionByIndex(variant, 2)?.value ?? "");
        break;

      default:
        break;
    }
  });
}

/**
 * Select the best default variant for a product: first `availableForSale`,
 * otherwise the first variant in the list.
 */
export function pickDefaultVariant(product: Product): ProductVariant | null {
  if (product.variants.length === 0) return null;
  return product.variants.find((v) => v.availableForSale) ?? product.variants[0] ?? null;
}

/**
 * Find the variant matching a set of selected option values. Returns `null`
 * if no variant matches exactly.
 */
export function findVariantByOptions(
  product: Product,
  selected: Record<string, string>,
): ProductVariant | null {
  for (const v of product.variants) {
    let match = true;
    for (const opt of v.selectedOptions) {
      if (selected[opt.name] !== undefined && selected[opt.name] !== opt.value) {
        match = false;
        break;
      }
    }
    if (match) return v;
  }
  return null;
}
